import type {JwsHeaderParameters, JwtPayload, Validator, Validators} from '../../types/jose/jwt';

export default (payload: JwtPayload, header: JwsHeaderParameters, validators: Validators): void => {
    for (const [key, validator] of Object.entries<Validator>(validators)) {
        if (validator !== false) {
            const value = key === 'alg' || key === 'typ' ? header[key] : payload[key];
            const valid =
                (typeof validator === 'string' && value === validator) ||
                (typeof validator === 'function' && validator(value, payload, header));

            if (!valid) {
                throw new Error(`Unexpected '${key}' value: ${JSON.stringify(value)}`);
            }
        }
    }
}

export const defaultValidators = (
    issuer: string,
    audience: string | string[],
    clockTolerance: number,
    maxTokenAge: number | undefined,
    strict: boolean,
    allowedSigningAlgs: string[] | undefined,
    tokenSigningAlg: string | undefined
): Validators => ({
    alg: (alg) => {
        return (
            (typeof alg === 'string') &&
            (alg.toLowerCase() !== 'none') &&
            ((allowedSigningAlgs === undefined) || allowedSigningAlgs.includes(alg)) &&
            ((tokenSigningAlg === undefined) || alg === tokenSigningAlg));
    },
    typ: (typ) => {
        return (!strict ||
            (typeof typ === 'string' && typ.toLowerCase().replace(/^application\//, '') === 'at+jwt'));
    },
    iss: (iss) => {
        return (typeof iss === 'string' && iss === issuer);
    },
    aud: (aud) => {
        audience = typeof audience === 'string' ? [audience] : audience;
        if (typeof aud === 'string') {
            return audience.includes(aud);
        }
        if (Array.isArray(aud)) {
            return audience.some(Set.prototype.has.bind(new Set(aud)));
        }
        return false;
    },
    exp: (exp) => {
        const now = Math.floor(Date.now() / 1000);
        return (typeof exp === 'number' && exp >= now - clockTolerance);
    },
    iat: (iat) => {
        if (maxTokenAge === undefined) {
            return (iat === undefined && !strict) || (typeof iat === 'number');
        }
        const now = Math.floor(Date.now() / 1000);
        return (
            typeof iat === 'number' &&
            iat < now + clockTolerance &&
            iat > now - clockTolerance - maxTokenAge
        );
    },
    sub: (sub) => (sub === undefined && !strict) || (typeof sub === 'string'),
    jti: (jti) => (jti === undefined && !strict) || (typeof jti === 'string')
});
